import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import axios, { AxiosInstance } from "axios";
import {
  AuthResponse,
  AuthState,
  LoginCredentials,
  UserRole,
  User,
} from "./types";

import { jwtDecode } from "jwt-decode";

const AuthContext = createContext<AuthState | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const axiosInstance: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token"); // Retrieve token from localStorage
    if (token) {
      config.headers.Authorization = `${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // A failed sign-in is answered with 401 too. That is a wrong email or
    // password, not an expired session: reloading /signin here would wipe the
    // error message the sign-in page is about to show, so leave it to login().
    const isSigninRequest = String(error.config?.url ?? "").endsWith(
      "auth/signin",
    );
    if (error.response?.status === 401 && !isSigninRequest) {
      window.dispatchEvent(new CustomEvent("unauthorized"));
      localStorage.removeItem("token"); // Clear token on 401
      window.location.href = "/signin"; // Redirect to sign-in
    }
    return Promise.reject(error);
  },
);

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true); // Add loading state
  const [logoutTimeout, setLogoutTimeout] = useState<number | null>(null);

  const isTokenExpired = (token: string): boolean => {
    try {
      const decoded: { exp: number } = jwtDecode(token);
      return Date.now() >= decoded.exp * 1000;
    } catch (error) {
      console.error("Error checking token expiration:", error);
      return true; // If we can't decode, assume expired for safety
    }
  };

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);
      try {
        const storedToken = localStorage.getItem("token");

        if (storedToken && !isTokenExpired(storedToken)) {
          setToken(storedToken);

          try {
            const decoded = jwtDecode<{
              sub: string;
              firstName: string;
              lastName: string;
              email: string;
              phoneNumber: string;
              roles: string[];
            }>(storedToken);

            setUser({
              id: decoded.sub,
              firstName: decoded.firstName,
              lastName: decoded.lastName,
              email: decoded.email,
              phoneNumber: decoded.phoneNumber,
              roles: decoded.roles,
            });
          } catch (error) {
            console.error("Failed to decode token:", error);
            logout();
          }
        } else if (storedToken) {
          // Token is expired
          logout();
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    if (!token) {
      if (logoutTimeout) {
        clearTimeout(logoutTimeout);
      }
    }
  }, [token, logoutTimeout]);

  const login = async (credentials: LoginCredentials): Promise<void> => {
    try {
      const response = await axiosInstance.post<AuthResponse>(
        "auth/signin",
        credentials,
      );
      const { accessToken, tokenType, ...userData } = response.data;

      if (isTokenExpired(accessToken)) {
        throw new Error("Token has expired");
      }

      // Store token with its type
      const fullToken = `${tokenType} ${accessToken}`;
      setToken(fullToken);

      setUser(userData);

      // Store in localStorage
      localStorage.setItem("token", fullToken);

      axiosInstance.defaults.headers.common.Authorization = fullToken;

      const decoded: { exp: number } = jwtDecode(accessToken);
      const expiresIn = decoded.exp * 1000 - Date.now();
      setTimeout(logout, expiresIn);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const backendError =
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Invalid email or password";
        throw new Error(backendError);
      }
      throw error;
    }
  };

  const logout = (): void => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("token");
    delete axiosInstance.defaults.headers.common.Authorization;
    window.location.href = "/signin";
  };

  const value: AuthState = {
    token,
    user,
    login,
    logout,
    loading, // Export loading state
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook for using auth context
export const useAuth = (): AuthState => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthProvider;
